import { useNavigate, Link } from "react-router-dom";
import GroupCard from "./cards/GroupCard";
import { useEffect, useState } from "react";
import {
  handlegetAllGroup,
  handleJoinGroup as apiJoinGroup,
  handleLeaveGroup as apiLeaveGroup,
} from "../../services/group";
import { isAuthenticated } from "../../utils/helpers";
import { getUserId } from "../../utils/helpers";
import { successToast, errorToast } from "../../utils/toasters";

export default function GroupsSection() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [processingGroups, setProcessingGroups] = useState(new Set());

  //for handling getAllGroups
  const getAllGroups = async () => {
    try {
      const geohash = localStorage.getItem("geohash") || "9v6m";
      setLoading(true);
      const payload = {
        geoHash: geohash,
        ...(isAuthenticated() && { userId: getUserId() }),
      };
      const response = await handlegetAllGroup(payload);
      const groupsData = response?.data?.groups || [];
      setGroups(groupsData);
    } catch (err) {
      setError(err.message || "Failed to fetch groups");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    getAllGroups();
  }, []);

  //for handling joinGroup
  const handleJoinGroup = async (groupId) => {
    if (!isAuthenticated()) {
      navigate("/signin");
      return;
    }
    setProcessingGroups((prev) => new Set(prev).add(groupId));
    setGroups((prevGroups) =>
      prevGroups.map((group) =>
        group.groupid === groupId
          ? {
              ...group,
              isJoined: true,
              member_count: String(parseInt(group.member_count || 0) + 1),
            }
          : group
      )
    );

    try {
      await apiJoinGroup(groupId);
      successToast("Successfully joined the group!");
    } catch (error) {
      errorToast("Failed to join group");
      setGroups((prevGroups) =>
        prevGroups.map((group) =>
          group.groupid === groupId
            ? {
                ...group,
                isJoined: false,
                member_count: String(
                  Math.max(0, parseInt(group.member_count || 0) - 1)
                ),
              }
            : group
        )
      );
    } finally {
      setProcessingGroups((prev) => {
        const next = new Set(prev);
        next.delete(groupId);
        return next;
      });
    }
  };

  //for handling LeaveGroup
  const handleLeaveGroup = async (groupId) => {
    if (!isAuthenticated()) {
      navigate("/signin");
      return;
    }
    setProcessingGroups((prev) => new Set(prev).add(groupId));
    setGroups((prevGroups) =>
      prevGroups.map((group) =>
        group.groupid === groupId
          ? {
              ...group,
              isJoined: false,
              member_count: String(
                Math.max(0, parseInt(group.member_count || 0) - 1)
              ),
            }
          : group
      )
    );

    try {
      await apiLeaveGroup(groupId);
      successToast("Successfully left the group!");
    } catch (error) {
      errorToast("Failed to leave group");
      setGroups((prevGroups) =>
        prevGroups.map((group) =>
          group.groupid === groupId
            ? {
                ...group,
                isJoined: true,
                member_count: String(parseInt(group.member_count || 0) + 1),
              }
            : group
        )
      );
    } finally {
      setProcessingGroups((prev) => {
        const next = new Set(prev);
        next.delete(groupId);
        return next;
      });
    }
  };

  return (
    <div className="bg-gray-50" style={{ 
      paddingTop: 'clamp(96px, 8.33vw, 160px)',
      paddingBottom: 'clamp(96px, 8.33vw, 160px)',
      paddingLeft: 'clamp(16px, 1.25vw, 24px)',
      paddingRight: 'clamp(16px, 1.25vw, 24px)'
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:wght@700&family=Afacad:wght@400;500;600&display=swap');
        
        .fraunces-bold {
          font-family: 'Fraunces', serif;
          font-weight: 700;
        }
        
        .afacad-font {
          font-family: 'Afacad', sans-serif;
        }
      `}</style>

      <div className="container mx-auto" style={{ maxWidth: '1920px', margin: '0 auto' }}>
        {/* Header */}
        <div className="text-center" style={{ marginBottom: 'clamp(32px, 2.78vw, 54px)' }}>
          <div className="flex justify-center mb-3 sm:mb-4 md:mb-5 lg:mb-6">
            <button
              className="afacad-font text-white rounded-full font-semibold"
              style={{ 
                backgroundColor: "#7B189F",
                width: 'clamp(120px, 8.08vw, 160px)',
                height: 'clamp(20px, 1.41vw, 28px)',
                fontSize: 'clamp(10px, 0.71vw, 14px)',
                fontWeight: 600,
                lineHeight: 'clamp(20px, 1.41vw, 28px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              Reach people like you
            </button>
          </div>
          <h2
            className="fraunces-bold"
            style={{ 
              color: "#7B189F",
              fontSize: 'clamp(18px, 1.31vw, 26px)',
              fontWeight: 700,
              lineHeight: '1.2',
              marginBottom: 'clamp(12px, 0.83vw, 16px)'
            }}
          >
            Anonymous Chat, Meet new people
          </h2>
          <p className="text-gray-600 max-w-2xl sm:max-w-3xl lg:max-w-4xl xl:max-w-5xl mx-auto px-4 sm:px-6 fraunces-bold" style={{ 
            fontSize: 'clamp(16px, 1.11vw, 22px)', 
            fontWeight: 700,
            lineHeight: '1.4',
            marginTop: 0
          }}>
            Find strangers worldwide, the new modern Omegle and OmeTV
            alternative. Connect with real people, enjoy ad free text and video
            chats, and build genuine friendships.
          </p>
        </div>

        {/* Groups Grid - Responsive grid layout */}
        {loading ? (
          <div className="flex justify-center items-center min-h-[200px] sm:min-h-[300px] md:min-h-[400px]">
            <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 border-4 border-[#7B189F] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" style={{ 
            gap: 'clamp(12px, 1.04vw, 20px)'
          }}>
            {groups.slice(0, 6).map((group) => (
              <GroupCard
                key={group.groupid}
                group={group}
                onJoin={handleJoinGroup}
                onLeave={handleLeaveGroup}
                isProcessing={processingGroups.has(group.groupid)}
              />
            ))}
          </div>
        )}

        {/* View All Groups Button */}
        <div className="text-center" style={{ marginTop: 'clamp(32px, 2.78vw, 54px)' }}>
          <Link
            to="/groups"
            className="afacad-font hover:opacity-90 transition-opacity inline-flex items-center justify-center gap-2"
            style={{ 
              color: "#411B26",
              fontSize: 'clamp(12px, 0.83vw, 16px)',
              fontWeight: 600
            }}
          >
            View All Groups
            <img
              src="/landing/more.png"
              alt="More"
              style={{ width: 'clamp(30px, 2.17vw, 43px)', height: 'clamp(30px, 2.17vw, 43px)' }}
            />
          </Link>
        </div>
      </div>
    </div>
  );
}
